import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { useInsideSheet } from "./sheetContext";
import { Check, ChevronDown, Plus, X } from "lucide-react";
import useAnchoredPanel, {
  anchoredPanelStyle,
  sheetStyle,
  useTakeoverStyle,
} from "./useAnchoredPanel";

export interface DropdownOption {
  value: string;
  label: string;
  /** Second line under the label, e.g. a price or a hint. */
  subtitle?: string;
  /**
   * Section heading shown above this option when it differs from the option
   * before it — "Popular" ahead of the dozen banks most people mean, "All
   * banks" ahead of the RBI's remaining thousand-odd. Omit for a plain,
   * unsectioned list.
   */
  group?: string;
  /**
   * A mark set at the right end of the row, for something true of this option
   * that its words do not say — the dish field's "this one is yours".
   *
   * Not a second label and not a group: it marks rows that are scattered
   * through the list rather than gathered into a section, which is exactly the
   * case a heading cannot cover. Give it its own words for a screen reader
   * (an `sr-only` span beside an `aria-hidden` glyph) — the mark is part of
   * what the row says, and a bare icon says nothing.
   */
  badge?: React.ReactNode;
  /**
   * Shown, but not an answer: the row is greyed, cannot be clicked, and the
   * keyboard steps over it.
   *
   * Drawn rather than filtered out because a row that is there and cannot be
   * taken says more than one that is gone. An appointment time already
   * booked, left in place and greyed, reads as "that hour is spoken for"; the
   * same row quietly removed says only that the list is shorter than it was,
   * and leaves somebody hunting for a time that has simply vanished.
   *
   * Greyed and nothing else — no strike, no mark. The list is read by looking
   * for the rows that are still black, and anything drawn over the others is
   * ink spent on what cannot be chosen.
   */
  disabled?: boolean;
  /**
   * Why it cannot be chosen, carried on the row's `title` and read out with
   * it. On hover only, and deliberately: the list is scanned for the row that
   * can be picked, and a reason printed under every closed one turns a list of
   * times into a wall of sentences. The row says it is closed by being struck
   * through; the reason is there for whoever asks.
   */
  disabledReason?: string;
}

/**
 * A row pinned under the option list, for the way out of it — the create
 * dialog's "Add custom service", which hands the field over to be typed into.
 *
 * It is not an option and never becomes the value: picking it closes the list
 * and calls back, leaving what to do next entirely to the caller. That is also
 * why it sits outside the `listbox` rather than being announced as one more
 * thing on it.
 */
export interface DropdownFooterAction {
  label: string;
  /**
   * Handed whatever the box was showing when the row was pressed — the filter
   * a searchable control had been typed into, which on this row is nearly
   * always the name the list failed to hold. Empty for a plain dropdown, and
   * for a searchable one that was opened and not typed into.
   */
  onSelect: (search: string) => void;
}

export interface CustomDropdownProps {
  options: DropdownOption[];
  value: string;
  onChange: (value: string) => void;
  /**
   * Stands in for a native select's blur: fires when the list closes, however
   * it closed, and when focus leaves the trigger. Forms use it to mark a field
   * visited, so it is called at most once per interaction but may repeat —
   * treat it as idempotent.
   */
  onBlur?: () => void;
  placeholder?: string;
  disabled?: boolean;
  /**
   * Called when a disabled control is reached for, instead of the list
   * opening. Supplying it changes how `disabled` is enforced: the browser
   * dispatches nothing to a natively disabled control, so one that has a
   * reason to give has to stay reachable to give it.
   */
  onDisabledClick?: () => void;
  /**
   * The row to open the list looking at while nothing has been chosen yet.
   *
   * For a long list whose useful part is nowhere near the top: every quarter
   * hour of a day is ninety-six rows, and an appointment is almost never
   * booked at one in the morning, so a list that opened at midnight asked to
   * be scrolled past a third of a day before it said anything useful.
   *
   * It scrolls and nothing else. The field stays unanswered and the row is not
   * highlighted, because a dropdown that quietly answered its own question
   * would be worse than one that opens in the wrong place.
   *
   * Ignored once there is a value: a list opened over an answer opens at the
   * answer, which is the stronger claim of the two.
   */
  openAt?: string;
  /** Turns the trigger into a text input that filters the list as you type. */
  searchable?: boolean;
  /**
   * Lets the typed text be used as the value even when the list does not hold
   * it, offered as a "Use …" row under whatever did match. For lists that
   * cover the common cases but cannot claim to be exhaustive. Searchable only
   * — there is nothing to type into otherwise.
   */
  creatable?: boolean;
  /** A row pinned under the list — see `DropdownFooterAction`. */
  footerAction?: DropdownFooterAction;
  /**
   * A band pinned above the list, shown whenever the panel is open — the key
   * to whatever `badge` is marking rows with.
   *
   * Pinned rather than scrolled with the rows, and for the same reason
   * `footerAction` is: a key that scrolls away is a key you have to scroll
   * back for. It stays put through an empty list too, since a reader who has
   * filtered everything out has not stopped needing to know what the mark
   * meant.
   *
   * It is outside the `listbox` and holds nothing pressable: it explains the
   * list rather than being part of it.
   */
  listHeader?: React.ReactNode;
  /**
   * Skips the built-in prefix/contains filter and shows `options` exactly as
   * given — for a list a caller has already filtered itself, e.g. against a
   * server-side search. `searchable` only; a plain dropdown has nothing to
   * skip.
   */
  remoteFilter?: boolean;
  /**
   * Fires with the trimmed search text whenever the list is open and the text
   * changes — including the moment it opens, seeded with the field's current
   * value — so a remote-backed list can debounce its own fetch off it. Read
   * through a ref internally, so a new function every render does not restart
   * anything.
   */
  onSearchChange?: (query: string) => void;
  /** Shown in place of "No results found" while a remote search is in flight. */
  loading?: boolean;
  /**
   * What the open list says when there is nothing in it at all — as opposed
   * to a search that matched nothing, which stays "No results found". A list
   * with no rows to offer has something to explain ("No active clients yet")
   * that a search miss does not; without this, both read the same and an
   * empty list looks like a failed search.
   */
  emptyMessage?: React.ReactNode;
  /**
   * Called when the list is scrolled to its end — a remote list's cue to fetch
   * its next page. Fires alongside the local `growList`, which is a no-op once
   * `remoteFilter` has put every fetched row on screen already.
   */
  onEndReached?: () => void;
  invalid?: boolean;
  /**
   * What the phone picker calls itself. A searchable list takes the whole
   * screen there, and a screen that has taken over a form has to say which
   * question it is asking — so this is the field's own label, passed down
   * because a control cannot read the `<label>` pointing at it.
   */
  heading?: string;
  /** Shorter, smaller-typed trigger for controls sitting in a table row. */
  size?: DropdownSize;
  /**
   * Drops the trigger's own border, ground and side padding, for a control
   * that is drawn inside somebody else's box — the supplement card's Dose
   * field, where a figure is typed on the left and its unit picked on the
   * right of one shared frame.
   *
   * The box around it owns the border, so it is the box that has to light up
   * on focus (`focus-within`) and redden when the field is wrong — which is
   * why `invalid` goes unread here rather than drawing a second outline
   * inside the first. The list still opens under the trigger's own half, but
   * from the box's bottom edge — its parent element — rather than from
   * inside the frame. See `panelRect`.
   */
  bare?: boolean;
  /**
   * Drops the tick beside the chosen row, leaving its tint to say which one
   * it is. For a short list on a toolbar - the editor's block style and
   * size - where the rows are a handful of words and the answer is already
   * written on the trigger above them; a tick there is a third way of
   * saying the same thing in a list four rows long.
   */
  plainSelection?: boolean;
  className?: string;
  /**
   * Tallest the option list can get before it scrolls, in px. An option row is
   * about 40px, so the default shows a little under eight of them.
   */
  maxHeight?: number;
  /**
   * How many options are put in the DOM at once, and how many more arrive
   * each time the list is scrolled to its end. The default clears every list
   * in the app bar the banks', which runs to about fifteen hundred — building
   * that many rows on open, and again on every keystroke, is what makes a
   * long list feel broken.
   */
  optionPageSize?: number;
  id?: string;
  "aria-labelledby"?: string;
  "aria-label"?: string;
  "aria-describedby"?: string;
}

/**
 * How much room the trigger takes. `md` is the form control every screen is
 * built from; `sm` is for controls packed into a table row, where a form-sized
 * box would push a table of eight columns past the fold. They meet again at
 * `3xl`, where there is space for the full one either way.
 */
export type DropdownSize = "md" | "sm";

/**
 * What was actually typed into a box that was holding an answer.
 *
 * A picker opens with the answer already given in its box and the text
 * selected, so the next keystroke is meant to replace it — and on a phone that
 * is very often not what happens. A tap to put the caret where the reader wants
 * it collapses the selection first, so the letter lands *beside* the answer
 * rather than instead of it: "Mumbai" becomes "Mumbain", which matches no city
 * at all, and a search that was just started comes back empty.
 *
 * The seed is known, so the difference is recoverable. Wherever the answer
 * survived the keystroke, what is left of the box is the search; where it did
 * not — the selection was honoured, or somebody is deleting their way through
 * it — the box is taken as it stands.
 */
const searchFrom = (next: string, seed: string): string =>
  seed && next.includes(seed) ? next.replace(seed, "") : next;

const triggerSizeClasses: Record<DropdownSize, string> = {
  md: "px-3.5 py-2.5 3xl:py-3 text-[14px] 3xl:text-[15px] ",
  sm: "px-3 py-1.5 text-[13px] 3xl:px-3.5 3xl:py-3 3xl:text-[15px] ",
};

/**
 * The trigger's look, exported so `MultiSelect` wears exactly the same one.
 * Two controls sitting in the same row of a form have to be indistinguishable
 * apart from what they do, and a second copy of these classes is how that
 * quietly stops being true.
 */
export const dropdownTriggerClasses = (
  invalid: boolean,
  disabled: boolean,
  size: DropdownSize = "md"
) =>
  "w-full flex items-center justify-between gap-2 rounded-[6px] border " +
  "text-left focus:outline-none transition-colors " +
  triggerSizeClasses[size] +
  // One ground or the other, never both. `bg-white` used to be in the string
  // unconditionally with the grey appended after it, and two background
  // utilities on one element are settled by which of them Tailwind happens to
  // emit last in the sheet rather than by the order they are written here —
  // white won, so a refused dropdown was drawn exactly like a live one. The
  // text fields beside it escaped that only because theirs is a `disabled:`
  // variant, which outranks the plain utility whatever the order.
  (disabled
    ? "cursor-not-allowed bg-[#F7F9FA] text-ink-muted "
    : "bg-white ") +
  (invalid
    ? "border-red-400 focus:border-red-500"
    : "border-line focus:border-brand");

/**
 * The same trigger with nothing drawn around it — see `bare`. It keeps the
 * type size and the row layout, so the value and its chevron sit exactly where
 * they would in a boxed one; everything that paints (border, ground, side
 * padding) belongs to the box it has been put inside.
 */
const bareTriggerClasses = (disabled: boolean, size: DropdownSize = "md") =>
  "w-full flex items-center justify-between gap-2 bg-transparent " +
  "text-left focus:outline-none transition-colors " +
  (size === "sm" ? "text-[13px] 3xl:text-[15px] " : "text-[14px] 3xl:text-[15px] ") +
  (disabled ? "cursor-not-allowed text-ink-muted" : "");

/**
 * Select control with a portalled option list, ported from the Triva panel's
 * `CustomDropdown`. It replaces the native `<select>` so the open list can be
 * styled — the panel flips above the trigger when there is no room below.
 *
 * Keyboard behaviour matches the native control it stands in for: arrows move
 * the active option, Enter picks it, Escape closes without changing the value.
 */
const CustomDropdown: React.FC<CustomDropdownProps> = ({
  options,
  value,
  onChange,
  onBlur,
  placeholder = "Select option",
  disabled = false,
  onDisabledClick,
  openAt,
  searchable = false,
  creatable = false,
  footerAction,
  listHeader,
  remoteFilter = false,
  onSearchChange,
  loading = false,
  emptyMessage,
  onEndReached,
  invalid = false,
  heading,
  size = "md",
  bare = false,
  plainSelection = false,
  className = "",
  maxHeight = 320,
  optionPageSize = 60,
  id,
  ...aria
}) => {
  const [open, setOpen] = useState(false);
  // The same answer, readable before the render that carries it.
  //
  // Focus can be taken away in the same breath it was given — a dialog
  // restoring it to the trigger, then claiming it back for its own panel — and
  // the blur handler below has to know the list was opened in between. Read
  // off state it would see the value from the render it was built in, which is
  // the one before any of that happened.
  const openRef = useRef(false);
  const [search, setSearch] = useState("");
  /**
   * Whether the box has been typed at since the list opened.
   *
   * It opens holding the answer already given — that is what a reader wants to
   * see, and with the text selected it is one keystroke from being replaced —
   * but that text is not a search anybody performed, so it does not narrow
   * anything. A list opened over a filled City shows every city, with the one
   * that is currently the answer ticked; the moment a key is pressed, what is
   * in the box becomes a filter like any other.
   */
  const [edited, setEdited] = useState(false);
  // Which option the keyboard is on. -1 while nothing is highlighted.
  const [activeIndex, setActiveIndex] = useState(-1);
  // How far down the matches have been built so far. Grows as the list is
  // scrolled or arrowed to its end.
  const [renderLimit, setRenderLimit] = useState(optionPageSize);

  const listId = `${React.useId()}-list`;

  const triggerRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  /** The box a sheet carries at its head — see `sheet` just below. */
  const sheetInputRef = useRef<HTMLInputElement>(null);
  /** The scrolling list itself — see the effect that opens it at the answer. */
  const listRef = useRef<HTMLDivElement>(null);
  /** The row holding the answer, so a list can be opened looking at it. */
  const selectedRowRef = useRef<HTMLButtonElement>(null);
  /** Whether this opening has already been scrolled to that row. */
  const lookedAt = useRef(false);

  // `sheet`: a phone, where the list is drawn at the foot of the screen rather
  // than hung off the field. Everything that differs between the two is marked
  // with it — see `useAnchoredPanel`, which explains why.
  const { rect, openUpward, sheet } = useAnchoredPanel(
    triggerRef,
    open,
    maxHeight
  );

  /**
   * Where the list hangs from.
   *
   * A bare trigger is one half of a control, and its own box is the bit of
   * the control with the words in — inside the frame's padding, so a list
   * dropped from its bottom edge opens over the frame's bottom border. The
   * list takes the trigger's left and width, which is the half it belongs
   * to, and the frame's top and bottom, which is where the control ends.
   */
  const panelRect = useMemo((): DOMRect | null => {
    const box = bare ? triggerRef.current?.parentElement : null;
    if (!rect || !box) return rect;

    const frame = box.getBoundingClientRect();
    const { left, width } = rect;
    const { top, bottom, height } = frame;
    // Spelled out rather than `new DOMRect`, which the test runner's DOM
    // does not have.
    return {
      x: left,
      y: top,
      left,
      top,
      right: left + width,
      bottom,
      width,
      height,
      toJSON: () => ({ left, top, width, height }),
    } as DOMRect;
  }, [bare, rect]);

  // A disabled control with something to say cannot carry the native
  // attribute: the browser fires no events at one, so the click that would
  // ask why never lands. It is marked `aria-disabled` instead — which reads
  // the same to a screen reader — and every route into the list is turned
  // back by hand. Without a reason to give, `disabled` stays the plain thing.
  const softDisabled = disabled && !!onDisabledClick;
  const nativeDisabled = disabled && !onDisabledClick;

  const selected = options.find((option) => option.value === value);

  /**
   * The row the list opens looking at: the answer already held, or — while
   * there is none — whatever `openAt` names. One value rather than two checks,
   * because the row has to be built before it can be scrolled to and the
   * effects below must agree with the ref on which row that is.
   */
  const lookAt = value || openAt || "";

  // A creatable control is expected to hold values the list has never heard
  // of, so it shows what it holds rather than falling back to the placeholder
  // the way a closed list does.
  const selectedLabel = selected?.label ?? (creatable ? value : "");

  const { visible, matchCount, selectedRank } = useMemo(() => {
    // The caller has already matched `options` against the query itself — a
    // second, local pass would only throw away rows the backend matched on
    // something other than a literal prefix or substring of the label.
    if (remoteFilter) {
      return {
        visible: options,
        matchCount: options.length,
        selectedRank: -1,
      };
    }

    // Seeded, not searched — see `edited`.
    const query = edited ? search.trim() : "";
    const lower = query.toLowerCase();

    // Matched on a prefix first, so typing "hdfc" opens on HDFC Bank rather
    // than on whatever co-operative bank happens to mention it and sorts
    // earlier. Both kinds of match are kept — just in that order.
    const starts: DropdownOption[] = [];
    const contains: DropdownOption[] = [];

    /**
     * One row per value, whatever the caller handed over.
     *
     * A list is keyed by its values, so the same value twice is not an untidy
     * list — it is a broken one: React reconciles the two rows as one and the
     * rows around them come back in an order nobody asked for. The city list
     * carried a hundred-odd repeats (the same name in two states) and read
     * perfectly for its first sixty rows, which is exactly how far a list has
     * to be built before the first of them shows up. Fixed at the source as
     * well — see `ALL_CITIES` — and refused here, because a control that falls
     * apart on a caller's duplicate is a trap for the next one.
     */
    const seen = new Set<string>();
    const once = (option: DropdownOption): boolean => {
      if (seen.has(option.value)) return false;
      seen.add(option.value);
      return true;
    };

    if (!searchable || !lower) {
      starts.push(...options.filter(once));
    } else {
      options.forEach((option) => {
        const label = option.label.toLowerCase();
        if (label.startsWith(lower)) {
          if (once(option)) starts.push(option);
        } else if (
          label.includes(lower) ||
          option.subtitle?.toLowerCase().includes(lower)
        ) {
          if (once(option)) contains.push(option);
        }
      });
    }

    const matches = [...starts, ...contains];
    const rows = matches.slice(0, renderLimit);
    // How far down the matches that row is sitting. The list opens looking at
    // it, and cannot look at a row that has not been built — see the two
    // effects below.
    const selectedRank = lookAt
      ? matches.findIndex((option) => option.value === lookAt)
      : -1;

    // Nothing on the list is what was typed, so offer the typing itself. It
    // goes last, and is never held back by the page size: the whole point of
    // it is to be there when the list has let you down.
    const known = options.some((option) => option.label.toLowerCase() === lower);
    if (creatable && searchable && query && !known) {
      rows.push({ value: query, label: `Use "${query}"` });
    }

    return { visible: rows, matchCount: matches.length, selectedRank };
  }, [options, lookAt, search, edited, searchable, creatable, renderLimit, remoteFilter]);

  /** More rows, up to whatever is left. Returns whether any were added. */
  const growList = useCallback(() => {
    let grew = false;
    setRenderLimit((current) => {
      if (current >= matchCount) return current;
      grew = true;
      return Math.min(current + optionPageSize, matchCount);
    });
    return grew;
  }, [matchCount, optionPageSize]);

  // Every new query is a new list, so it starts from the top again — without
  // this, narrowing would keep whatever depth the last one was scrolled to.
  useEffect(() => {
    setRenderLimit(optionPageSize);
  }, [search, open, optionPageSize]);

  // Read through a ref rather than depended on directly below — a caller that
  // builds `onSearchChange` fresh every render (as a plain arrow function
  // naturally is) must not restart the search on every keystroke of its own.
  const onSearchChangeRef = useRef(onSearchChange);
  useEffect(() => {
    onSearchChangeRef.current = onSearchChange;
  }, [onSearchChange]);

  // Every open re-seeds the query off the current value, and every keystroke
  // after that is a new one — a remote-backed list debounces its own fetch
  // off exactly these two things. Silent while shut, so dismissing (which
  // also clears `search`) never fires one last search for nothing.
  useEffect(() => {
    if (open) onSearchChangeRef.current?.(search);
  }, [open, search]);

  /** Shuts the list and forgets the query, leaving the value as it stands. */
  const dismiss = useCallback(() => {
    openRef.current = false;
    setOpen(false);
    setSearch("");
    setEdited(false);
    setActiveIndex(-1);
    onBlur?.();
  }, [onBlur]);

  /**
   * Leaving the control. The box opens holding the current value, so finding
   * it empty here means it was deliberately erased — and clearing the field
   * is what that has to mean, or there would be no way to empty one.
   * Escape does not come through here: cancelling keeps the value.
   */
  const close = useCallback(() => {
    if (searchable && search === "" && value !== "") onChange("");
    dismiss();
  }, [searchable, search, value, onChange, dismiss]);

  /**
   * Opens the list seeded with what the field already holds, so reopening a
   * filled control shows its value instead of going blank. The text is
   * selected, so typing still starts a new search in one keystroke.
   *
   * What it does not do is treat that text as a search. Seeded *and* filtered
   * on, the list behind it came up narrowed to the one row it already held —
   * so reopening a filled City on a phone gave a screen with a search bar and
   * a single option clipped under it, which reads as a dropdown that failed to
   * open. The answer shows in the box, and the whole list shows under it, with
   * that answer ticked. See `edited`.
   */
  const openList = useCallback(() => {
    if (open) return;
    openRef.current = true;
    setSearch(selectedLabel);
    setEdited(false);
    setOpen(true);
  }, [open, selectedLabel]);

  /**
   * Enough of the list built to include the answer already held.
   *
   * The list is paged — sixty rows at a time, because twelve hundred of them
   * built on every keystroke is what makes a long list feel broken — and an
   * answer four hundred rows down was therefore not in the list at all. On a
   * desktop that hardly showed: the box opens holding the value, so the list
   * was already narrowed to it. A phone opens the list whole, and a picker that
   * cannot show you what you last chose is a picker that has forgotten it.
   */
  useEffect(() => {
    if (!open) return;
    if (selectedRank >= 0 && selectedRank >= renderLimit) {
      setRenderLimit(selectedRank + 1);
    }
  }, [open, selectedRank, renderLimit]);

  /**
   * And every query is read from the top of its own results.
   *
   * The list keeps whatever scroll it was left at, and a phone picker is opened
   * scrolled a long way down it — to the answer already given, which may be
   * hundreds of rows in. Type from there and the matches were built above a
   * list still parked in the four hundreds: what came back was the tail of a
   * short list, or blank space under it, rather than the handful of cities that
   * had just been searched for.
   */
  useEffect(() => {
    if (!open || !edited) return;
    const list = listRef.current;
    if (list) list.scrollTop = 0;
  }, [open, edited, search]);

  /**
   * And the list opens looking at that row rather than at the top of the
   * alphabet.
   *
   * On a desktop as well as on a phone. This was once the sheet's alone, on
   * the reasoning that a desktop box opens holding its value and so had
   * already narrowed the list to it — true of a list that is typed into, and
   * quite wrong for one that is scrolled. Every quarter hour of a day is
   * ninety-six rows, and the time dropdown opened at midnight however late in
   * the day it was being read.
   *
   * No dependency list: the row it needs may not have been built on the render
   * that opened the list — the effect above may still be paging towards it —
   * so this looks on every render until there is something to look at, and
   * then stops. Only while the filter is untouched: once somebody is typing,
   * the top of what they have narrowed it to is where they want to be.
   */
  useEffect(() => {
    if (!open) {
      lookedAt.current = false;
      return;
    }
    if (lookedAt.current || edited) return;

    const list = listRef.current;
    const row = selectedRowRef.current;
    if (!list || !row) return;

    // The list's own scroll, moved by hand, rather than `scrollIntoView`.
    //
    // That asks the browser to bring the row into view by whatever scrolling it
    // takes, and it takes it wherever it can: the panel around the list is a
    // scroll container too — `overflow-hidden` hides a scrollbar, it does not
    // refuse to scroll — so centring a row halfway down the list scrolled the
    // panel as well, carrying the heading and the box being typed into off the
    // top of the screen. Only the list may move here, and this is what says so.
    const listBox = list.getBoundingClientRect();
    const rowBox = row.getBoundingClientRect();
    // A third of the way down rather than halfway. On a phone the list runs to
    // the bottom of the screen and a keyboard covers the foot of it, so the
    // middle of the list is not the middle of what can be seen — an answer
    // centred there opens behind the keys. A third is above them on any
    // handset, and needs nothing measured to know it. On a desktop it reads as
    // a list opened at the row with a little of what comes before it still
    // showing, which is what somebody scrolling back a quarter of an hour
    // wants to find.
    list.scrollTop += rowBox.top - listBox.top - listBox.height / 3;

    lookedAt.current = true;
  });

  /**
   * The filter box takes the caret as the list opens, with what it holds
   * selected — so a reopened field shows its value and one keystroke replaces
   * it.
   *
   * Which box that is depends on how the list was drawn. A sheet carries its
   * own, and it has to: the trigger stays where it is on the page, and on a
   * phone that is very often the part of the page a keyboard is covering — you
   * would be typing into a field you cannot see. The sheet's box is above the
   * keyboard by construction.
   *
   * A layout effect rather than an ordinary one, because a phone browser only
   * opens the keyboard for a `focus()` it can still trace back to the tap that
   * caused it. Layout effects run inside the event; passive ones are flushed
   * after it, by which time the gesture has been spent and the keyboard stays
   * down.
   */
  useLayoutEffect(() => {
    if (!open || !searchable) return;
    const box = sheet ? sheetInputRef.current : inputRef.current;
    box?.focus();
    box?.select();
  }, [open, searchable, sheet]);

  const pick = (optionValue: string) => {
    // Nothing reachable should be able to get here with one — the row is
    // natively disabled and the keyboard steps over it — but a value picked
    // by any route that appears later must still be refused, because this is
    // the one door the answer goes through.
    if (options.some((option) => option.value === optionValue && option.disabled)) {
      return;
    }
    onChange(optionValue);
    // `dismiss`, not `close`: the value was just set, and the empty query it
    // was picked from is no reason to throw it away again.
    dismiss();
  };

  // To the keyboard the footer is one row past the last option, so arrowing
  // off the bottom of the list lands on it before wrapping back to the top.
  const footerIndex = footerAction ? visible.length : -1;
  const rowCount = visible.length + (footerAction ? 1 : 0);

  const runFooterAction = () => {
    // Read before dismissing, which is what clears it. A plain dropdown was
    // never typed into and hands back its selected label, so only the text a
    // searchable one is actually showing travels.
    const typed = searchable ? search.trim() : "";
    // Shut first: what the caller does next — swapping the control for a box
    // to type in, and focusing it — must not happen under an open list.
    dismiss();
    footerAction?.onSelect(typed);
  };

  // A pointer press anywhere outside both the trigger and the panel dismisses.
  // `mousedown` rather than `click` so the list closes before a click lands on
  // whatever is underneath it.
  useEffect(() => {
    if (!open) return;

    const handlePointerDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        !triggerRef.current?.contains(target) &&
        !panelRef.current?.contains(target)
      ) {
        close();
      }
    };

    document.addEventListener("mousedown", handlePointerDown);
    return () => document.removeEventListener("mousedown", handlePointerDown);
  }, [open, close]);

  /**
   * Focus leaving the control, which shuts the list however it was opened.
   *
   * The options and the footer row keep focus on the trigger by refusing their
   * own mousedown, so this never fires for a list being clicked through — only
   * for focus that has genuinely gone elsewhere, and a list left open under
   * whatever took it is a menu floating over a screen it no longer belongs to.
   *
   * `dismiss` rather than `close`: focus being taken is not somebody clearing
   * the field, so the value stands however the box was left.
   */
  const handleBlur = (event: React.FocusEvent) => {
    if (disabled) return;
    if (panelRef.current?.contains(event.relatedTarget as Node | null)) return;
    // `dismiss` reports the blur itself, so it is not reported twice.
    if (openRef.current) dismiss();
    else onBlur?.();
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (disabled) {
      // The keys that would have opened the list are the keyboard's version
      // of reaching for it, so they get the same answer a click does.
      if (["ArrowDown", "ArrowUp", "Enter"].includes(event.key)) {
        event.preventDefault();
        onDisabledClick?.();
      }
      return;
    }

    if (event.key === "Escape") {
      if (open) {
        event.stopPropagation();
        // Cancelling, so the value stands however the box was left.
        dismiss();
      }
      return;
    }

    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      if (!open) {
        openList();
        return;
      }
      const step = event.key === "ArrowDown" ? 1 : -1;

      // Arrowing off the bottom builds the next page rather than wrapping,
      // so the keyboard can reach everything the scroll wheel can.
      if (
        step === 1 &&
        activeIndex >= visible.length - 1 &&
        renderLimit < matchCount
      ) {
        growList();
        setActiveIndex(activeIndex + 1);
        return;
      }

      setActiveIndex((current) => {
        if (rowCount === 0) return -1;

        // Walks on until it finds a row that can be chosen, wrapping at both
        // ends so holding one arrow key cycles rather than dead-ending. The
        // footer is always reachable — it is not an option and is never
        // disabled. Bounded by the row count, so a list whose every option is
        // disabled stops where it started instead of spinning.
        let next = current;
        for (let step_count = 0; step_count < rowCount; step_count += 1) {
          next += step;
          if (next < 0) next = rowCount - 1;
          if (next >= rowCount) next = 0;
          if (next === footerIndex || !visible[next]?.disabled) return next;
        }
        return current;
      });
      return;
    }

    if (event.key === "Enter") {
      // An open list swallows the press whatever it goes on to do with it. The
      // control sits inside a form, and the key beside a phone keyboard's
      // space bar is a Go — a press meant for the option under the caret must
      // not send the form instead.
      if (open) event.preventDefault();

      if (open && activeIndex === footerIndex && footerAction) {
        runFooterAction();
      } else if (open && activeIndex >= 0 && visible[activeIndex]) {
        // `pick` refuses a disabled one anyway; stopping here keeps the list
        // open rather than swallowing the press and closing over nothing.
        if (!visible[activeIndex].disabled) pick(visible[activeIndex].value);
      } else if (!open) {
        event.preventDefault();
        openList();
      }
    }
  };

  /**
   * How the list is drawn, of three ways.
   *
   * A phone gets one of two. Which one turns on whether there is anything to
   * type into — because that is the same thing as whether a keyboard is
   * involved — and on what the field is already sitting inside.
   *
   * `takeover` is the whole screen: a heading, a filter box where there is one
   * to have, and the list under it. Nothing about it is measured against the
   * keyboard, so there is nothing for a browser to be wrong about — see
   * `takeoverStyle`. Two things ask for it:
   *
   *  - a searchable list, for the keyboard reason above.
   *  - a field that is already inside a bottom sheet. A second sheet there
   *    comes up at the same edge of the same screen, dims the panel that
   *    opened it and covers half of it — leaving two surfaces competing, with
   *    the question on the one that has just gone grey. The whole screen is
   *    unambiguous: one thing to answer, a heading saying what, and a way
   *    back. See `useInsideSheet`.
   *
   * A plain list of six options on an ordinary screen opens no keyboard and has
   * nothing over it, so it needs neither: it is a sheet at the foot of the
   * page, which is the lighter thing and reads as an answer to the field rather
   * than a new page.
   *
   * Everything wider than a phone keeps the list hung under its trigger, which
   * is right on a desktop and is what the dashboard has always shown.
   */
  const insideSheet = useInsideSheet();
  const takeover = sheet && (searchable || insideSheet);

  // Placed on the top of what can be seen rather than on the top of the
  // viewport, which a browser's own toolbar overlaps — see `useTakeoverStyle`.
  const takeoverPlacement = useTakeoverStyle(panelRef, open && takeover);

  /** What a takeover calls itself — see `heading`. */
  const title = heading ?? aria["aria-label"] ?? placeholder;

  const panelStyle = takeover
    ? takeoverPlacement
    : sheet
      ? sheetStyle()
      : panelRect
        ? anchoredPanelStyle(panelRect, openUpward, maxHeight)
        : null;

  const panel =
    open && panelStyle
      ? createPortal(
          <>
            {/* What is behind a sheet is dimmed and stops taking drags, so the
                form cannot scroll about under a list that is no longer
                measured against it. Pressing it dismisses, through the same
                outside-press handler everything else goes through — the
                backdrop is outside the panel too. */}
            {sheet && !takeover && (
              <div
                aria-hidden="true"
                className="fixed inset-0 z-[55] touch-none bg-[#0B1B1F]/25"
              />
            )}

            {/* The panel is the frame; the list inside it is what scrolls.
                They are two elements rather than one so a footer action can
                sit under the list without scrolling away with it —
                `overflow-hidden` here is what keeps the rounded corners
                clipping their contents. */}
            <div
              ref={panelRef}
              // A takeover is the screen: no border to draw against anything
              // beside it, no corners to round away from the glass, and no
              // shadow, since there is nothing showing for it to fall on.
              role={takeover ? "dialog" : undefined}
              aria-modal={takeover ? true : undefined}
              aria-label={takeover ? title : undefined}
              className={
                "z-[60] flex flex-col bg-white " +
                // `clip` for a picker, `hidden` everywhere else. They look the
                // same and are not the same: a box with `overflow: hidden` is
                // still a scroll container with its scrollbar taken off, and a
                // browser reaching for the focused box inside it can scroll the
                // frame itself — which slides the heading and the search box
                // off the top while the list below them stays where it is.
                // `overflow: clip` is not a scroll container at all, so there
                // is nothing left to scroll. It is only safe here because a
                // picker is square: nothing needs clipping to a rounded corner.
                (takeover ? "overflow-clip " : "overflow-hidden ") +
                (takeover
                  ? ""
                  : sheet
                    ? // Rounded along the top edge only: the other three are
                      // on the edge of the screen, and a corner turned away
                      // from the glass there is a sliver of form showing
                      // through the one thing meant to be sitting over it.
                      "rounded-t-2xl border-t border-line " +
                      // Clear of the home indicator. A sheet is always down on
                      // the glass now — see `sheetStyle`.
                      "pb-[env(safe-area-inset-bottom)] " +
                      "shadow-[0_-8px_32px_rgba(11,27,31,0.18)]"
                    : "rounded-md border border-line " +
                      "shadow-[0_8px_24px_rgba(11,27,31,0.12)]")
              }
              style={panelStyle}
              // And put back if anything does move it — see the same pin in
              // `useTakeoverStyle`, which catches the nudge that happens
              // before a scroll is ever reported.
              onScroll={
                takeover
                  ? (event) => {
                      if (event.currentTarget.scrollTop !== 0) {
                        event.currentTarget.scrollTop = 0;
                      }
                    }
                  : undefined
              }
            >
              {sheet && !takeover && (
                // The bar every phone sheet wears. It is what says this is a
                // sheet laid over the form rather than the page having jumped.
                <div
                  aria-hidden="true"
                  className="flex flex-shrink-0 justify-center pb-1 pt-2.5"
                >
                  <span className="h-1 w-9 rounded-full bg-[#E1E7EA]" />
                </div>
              )}

              {takeover && (
                /*
                 * What the screen is for, and the way back off it.
                 *
                 * A picker that has taken the page over owes the reader both:
                 * the question it is answering, because the field it belongs to
                 * is no longer on screen to say so, and a way out that is not a
                 * guess at where the edge of a panel is.
                 */
                <div className="flex flex-shrink-0 items-center gap-1 border-b border-line pl-4 pr-2 pt-[max(0.75rem,env(safe-area-inset-top))] pb-3">
                  <span className="min-w-0 flex-1 truncate text-[17px] font-semibold text-ink">
                    {title}
                  </span>
                  <button
                    type="button"
                    aria-label="Close"
                    // Keeps focus where it is, the same way the options do —
                    // the press is what closes this, not the blur.
                    onMouseDown={(event) => event.preventDefault()}
                    // `close`, not `dismiss`: this is leaving the control, and
                    // a box deliberately emptied on the way out is how an
                    // answer is taken back — the same as stepping off the
                    // field on a desktop.
                    onClick={close}
                    className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full text-ink-muted transition-colors hover:bg-[#F1F4F6] hover:text-ink"
                  >
                    <X aria-hidden="true" className="h-5 w-5" />
                  </button>
                </div>
              )}

              {takeover && searchable && (
                // The filter box, moved out of the trigger — see the focus
                // effect above for why it cannot stay there. Only where there
                // is something to search: a takeover that is one because it
                // opened inside a sheet is still a plain list of six options,
                // and a search box over six rows is a keyboard nobody asked
                // for.
                <div className="flex-shrink-0 px-4 pb-3 pt-3">
                  <input
                    ref={sheetInputRef}
                    type="text"
                    autoComplete="off"
                    // The keyboard's action key finishes with the list rather
                    // than offering to send the form the field sits in.
                    enterKeyHint="done"
                    aria-controls={listId}
                    aria-label={aria["aria-label"] ?? placeholder}
                    placeholder={placeholder}
                    value={search}
                    onChange={(event) => {
                      // The first keystroke on a box still holding the answer
                      // is the start of a search, however it landed — see
                      // `searchFrom`.
                      setSearch(
                        edited
                          ? event.target.value
                          : searchFrom(event.target.value, search)
                      );
                      setEdited(true);
                      setActiveIndex(-1);
                    }}
                    onBlur={handleBlur}
                    onKeyDown={handleKeyDown}
                    // 16px like every other control on a public form: Safari
                    // zooms the page into anything smaller and never zooms
                    // back out — see `publicForm.css`.
                    className="w-full rounded-[6px] border border-line px-3.5 py-2.5 text-[16px] text-ink placeholder:text-ink-subtle focus:border-brand focus:outline-none"
                  />
                </div>
              )}

              {/* Above the list and outside it — see `listHeader`. */}
              {listHeader && (
                <div className="flex flex-shrink-0 items-center gap-2 border-b border-[#E0E0E0] bg-[#F8FEFF] px-4 py-2 text-[12px] text-ink-muted">
                  {listHeader}
                </div>
              )}

              <div
                ref={listRef}
                id={listId}
                role="listbox"
                aria-label={aria["aria-label"]}
                // The next page is built as the last one is reached, so a long
                // list is scrolled through rather than typed past. The margin is
                // roughly a row, which is enough to land the rows before the
                // scrollbar reaches the bottom.
                onScroll={(event) => {
                  const panel = event.currentTarget;
                  const remaining =
                    panel.scrollHeight - panel.scrollTop - panel.clientHeight;
                  if (remaining < 48) {
                    growList();
                    onEndReached?.();
                  }
                }}
                className={
                  "min-h-0 flex-1 overflow-y-auto overscroll-contain py-1 custom-scrollbar-overlay" +
                  // A takeover runs to the bottom edge of the screen, so its
                  // last row would otherwise sit under the home indicator on
                  // the phones that have one. Costs nothing while a keyboard
                  // is covering that edge anyway.
                  (takeover ? " pb-[env(safe-area-inset-bottom)]" : "")
                }
              >
                {visible.length === 0 ? (
                  <p className="px-4 py-3 text-center text-[13px] text-ink-subtle">
                    {loading
                      ? "Searching…"
                      : options.length === 0 && emptyMessage != null
                      ? emptyMessage
                      : "No results found"}
                  </p>
                ) : (
                  visible.map((option, index) => {
                    const isSelected = option.value === value;
                    // A header only announces a change of section, so it takes
                    // no slot of its own in `visible` and cannot be arrowed to —
                    // the index driving keyboard nav is untouched by it.
                    const previousGroup = index > 0 ? visible[index - 1].group : undefined;
                    const showHeader = !!option.group && option.group !== previousGroup;
                    return (
                      <React.Fragment key={option.value}>
                        {showHeader && (
                          <p
                            className={`px-3 pb-1 text-[11px] font-medium uppercase tracking-wide text-ink-subtle ${
                              index === 0 ? "pt-1" : "pt-2.5"
                            }`}
                          >
                            {option.group}
                          </p>
                        )}
                        <button
                          type="button"
                          role="option"
                          aria-selected={isSelected}
                          // `aria-disabled` rather than the native attribute:
                          // a natively disabled button is dropped out of the
                          // accessibility tree in some browsers, and a row
                          // whose whole job is to say "this one is taken" has
                          // to still be announced. The press is turned back by
                          // hand below, and `title` carries the reason to a
                          // pointer.
                          aria-disabled={option.disabled || undefined}
                          title={option.disabledReason || undefined}
                          // The answer already held, so a list opened over a
                          // filled field can be scrolled to it — or, where
                          // there is no answer yet, the row `openAt` names.
                          ref={
                            lookAt && option.value === lookAt
                              ? selectedRowRef
                              : undefined
                          }
                          // Keeps focus on the trigger, so the control never
                          // loses its focus ring while the list is clicked
                          // through.
                          onMouseDown={(event) => event.preventDefault()}
                          // A phone has no pointer to follow, and a tap fires
                          // this at whatever it landed on and leaves it there.
                          // The row a finger brushed past would then be the one
                          // wearing a highlight, which on a list where one row
                          // is genuinely the answer is worse than no highlight
                          // at all.
                          // A row that cannot be chosen does not take the
                          // highlight — and clears it rather than leaving it
                          // where it was. Doing nothing here looked like a
                          // hover that had stuck: the pointer moved on down
                          // the closed rows while the grey stayed behind on
                          // the last row it was allowed to sit on.
                          onMouseEnter={
                            sheet
                              ? undefined
                              : () => setActiveIndex(option.disabled ? -1 : index)
                          }
                          onClick={() => !option.disabled && pick(option.value)}
                          className={
                            "my-0.5 flex w-[calc(100%-1rem)] items-center gap-3 " +
                            "rounded-md text-left transition-colors " +
                            // A row worth putting a thumb on, where a thumb is
                            // what is used.
                            (sheet ? "mx-2 px-3 py-3 " : "mx-2 px-3 py-2 ") +
                            // Three states, and the brand colour belongs to
                            // exactly one of them: the answer. Where the
                            // keyboard is, and where a pointer is, are a
                            // neutral grey — they say "about to be chosen",
                            // which is not the same thing as chosen and must
                            // not look like it.
                            // A row that cannot be chosen takes none of the
                            // three: no brand fill and no highlight under the
                            // pointer. The cursor is left alone — every other
                            // row in this list is a plain arrow, and a barred
                            // circle on one of them is a louder refusal than
                            // the row is making.
                            (option.disabled
                              ? ""
                              : isSelected
                                ? "bg-brand-soft"
                                : index === activeIndex
                                  ? "bg-[#F1F4F6]"
                                  : sheet
                                    ? ""
                                    : "hover:bg-[#F1F4F6]")
                          }
                        >
                          <span className="min-w-0 flex-1">
                            <span
                              className={`block ${sheet ? "text-[15px]" : "text-[14px]"} ${
                                option.disabled
                                  ? "text-ink-subtle"
                                  : isSelected
                                    ? "text-brand-dark font-medium"
                                    : "text-ink"
                              }`}
                            >
                              {option.label}
                            </span>
                            {option.subtitle && (
                              <span className="mt-0.5 block text-[12px] text-ink-subtle">
                                {option.subtitle}
                              </span>
                            )}
                          </span>
                          {/* Whatever is true of this row that its words do not
                              say. Ahead of the tick rather than instead of it:
                              the two answer different questions — what this row
                              is, and whether it is the one chosen — and a row
                              can want both at once. */}
                          {option.badge && (
                            <span className="flex flex-shrink-0 items-center">
                              {option.badge}
                            </span>
                          )}
                          {/* And a mark, so which row is the answer survives
                              being read in a hurry, in a colour somebody
                              cannot see, or beside a row the keyboard happens
                              to be sitting on. */}
                          {isSelected && !plainSelection && (
                            <Check
                              aria-hidden="true"
                              className="h-4 w-4 flex-shrink-0 text-brand-dark"
                              strokeWidth={2.5}
                            />
                          )}
                        </button>
                      </React.Fragment>
                    );
                  })
                )}
              </div>

              {footerAction && (
                <button
                  type="button"
                  // Keeps focus on the trigger, the same way the options do.
                  onMouseDown={(event) => event.preventDefault()}
                  onMouseEnter={() => setActiveIndex(footerIndex)}
                  onClick={runFooterAction}
                  // `py-2` rather than anything roomier: the row is pinned, so
                  // whatever height it takes is height the list above it loses.
                  // This matches the padding of an option, which is what keeps
                  // it reading as part of the same list.
                  className={`flex w-full flex-shrink-0 items-center gap-2 border-t border-[#E0E0E0] px-4 py-2 text-left text-[14px] text-ink transition-colors ${
                    activeIndex === footerIndex
                      ? "bg-[#E2F6FA]"
                      : "bg-[#EFFBFD] hover:bg-[#E2F6FA]"
                  }`}
                >
                  <Plus aria-hidden="true" className="h-4 w-4 flex-shrink-0" />
                  {footerAction.label}
                </button>
              )}
            </div>
          </>,
          document.body
        )
      : null;

  const triggerClasses = bare
    ? bareTriggerClasses(disabled, size)
    : dropdownTriggerClasses(invalid, disabled, size);

  return (
    <div ref={triggerRef} className={`relative ${className}`}>
      {searchable ? (
        <div className={triggerClasses}>
          <input
            ref={inputRef}
            id={id}
            type="text"
            role="combobox"
            aria-expanded={open}
            aria-controls={listId}
            aria-invalid={invalid || undefined}
            autoComplete="off"
            disabled={nativeDisabled}
            aria-disabled={disabled || undefined}
            // `readOnly` is what stops a soft-disabled box being typed into,
            // the one thing `aria-disabled` alone does not prevent.
            //
            // And what keeps a phone's keyboard down on the tap that opens a
            // sheet. The sheet carries the box that is typed into, so this one
            // is a label from the moment it is pressed — a keyboard opened for
            // it here would be opened for a field about to be covered, and
            // shut and reopened a frame later for the sheet's.
            readOnly={softDisabled || sheet}
            placeholder={placeholder}
            // A value too long for the box is cut off by the box itself, with
            // no room for an ellipsis to say so — so the whole of it is on
            // hover. Only while the list is shut: what is being typed is
            // already whole in front of whoever is typing it.
            title={(!open && selectedLabel) || undefined}
            // What is being typed belongs in the box it is being typed into,
            // which while a picker is up is the picker's. The trigger keeps
            // showing the answer it holds.
            value={open && !sheet ? search : selectedLabel}
            onChange={(event) => {
              setSearch(event.target.value);
              setEdited(true);
              setActiveIndex(-1);
              if (!open) setOpen(true);
            }}
            // Ahead of focus, so the reason is given on the press rather than
            // after the caret has already landed in the box.
            onMouseDown={softDisabled ? () => onDisabledClick?.() : undefined}
            onFocus={() => !disabled && openList()}
            // Belt and braces for the tap that opens a sheet. The box is
            // `readOnly` there so no keyboard opens for it, and a handful of
            // in-app browsers do not focus a read-only field at all — which
            // would leave the field looking like a control and behaving like a
            // caption. `openList` is a no-op on a list already open, so this
            // costs nothing everywhere else.
            onClick={() => !disabled && openList()}
            // A refused control reports nothing: the field was never visited,
            // so it has no business turning red for being empty.
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
            className={`w-full bg-transparent text-ink placeholder:text-ink-subtle focus:outline-none ${
              softDisabled ? "cursor-not-allowed" : ""
            }`}
            {...aria}
          />
          <ChevronDown
            aria-hidden="true"
            className={`w-4 h-4 flex-shrink-0 text-ink-muted transition-transform ${
              open ? "rotate-180" : ""
            }`}
          />
        </div>
      ) : (
        <button
          id={id}
          type="button"
          role="combobox"
          aria-expanded={open}
          aria-controls={listId}
          aria-haspopup="listbox"
          aria-invalid={invalid || undefined}
          disabled={nativeDisabled}
          aria-disabled={disabled || undefined}
          onClick={() => {
            if (disabled) {
              onDisabledClick?.();
              return;
            }
            if (open) close();
            else openList();
          }}
          // A refused control reports nothing: the field was never visited, so
          // it has no business turning red for being empty.
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          // The label below is truncated to the trigger, so the whole of a
          // long one is read on hover.
          title={selectedLabel || undefined}
          className={triggerClasses}
          {...aria}
        >
          <span
            className={`min-w-0 flex-1 truncate ${
              selectedLabel ? "text-ink" : "text-ink-subtle"
            }`}
          >
            {selectedLabel || placeholder}
          </span>
          <ChevronDown
            aria-hidden="true"
            className={`w-4 h-4 flex-shrink-0 text-ink-muted transition-transform ${
              open ? "rotate-180" : ""
            }`}
          />
        </button>
      )}

      {panel}
    </div>
  );
};

export default CustomDropdown;
